import React, { useState, useEffect, useMemo } from 'react'
import View, { STATUS_FILTER_OPTIONS } from './View'
import { getSpies, InviteSpyByEmail } from './graphql'
import { Box } from '@material-ui/core'
import { toLocaleNonAmericanDateString } from '../../../../../shared/libraries/utils/date'
import { StatusLabel } from '../../../../../Components/StatusLabel'
import { LinkView } from '../../../../../Components/Link/View'
import { LinkEdit } from '../../../../../Components/Link/Edit'
import { ButtonDeleteInline } from '../../../../../Components/Buttons/ButtonDeleteInline'
import {
  showReduxModal,
  hideReduxModal,
  resetReduxModal,
} from 'Reducers/UI/actions'
import { useDispatch } from 'react-redux'
import { useAmbassadorsRequest } from '../../Ambasadors/gql'
import injectStyles from 'react-jss'
import { styles } from './style'
import {
  BRAND_AMBASSADOR_STATUS_ENUM,
  INVITED_SPY_STATUS,
} from '../../../../../shared/constants'
import { PATHS } from '../../../../../Config/Routing/paths'
import { urlGet, urlRemove } from '../../../../../Utils/URL'

const getInitialStatus = () => {
  const status = urlGet('Status')

  const statuses = STATUS_FILTER_OPTIONS.map(item => item.value)

  if (status && statuses.includes(status)) {
    return status
  } else if (status) {
    urlRemove('Status')
  }

  return null
}

const List = ({ cityId, match: { url }, classes, cityEmail }) => {
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({
    status: getInitialStatus(),
  })
  const [data, setData] = useState([])
  const [totalCount, setTotalCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [offset, setOffset] = useState(0)
  const [resultsPerPage, setResultsPerPage] = useState(50)
  const dispatch = useDispatch()

  const { deleteAmbassador } = useAmbassadorsRequest(cityId)

  useEffect(() => {
    fetchData()
  }, [search, filters, offset, resultsPerPage])

  const convertStatuses = status => {
    if (!status) {
      return null
    }

    if (status === INVITED_SPY_STATUS) {
      return [
        BRAND_AMBASSADOR_STATUS_ENUM.PENDING,
        BRAND_AMBASSADOR_STATUS_ENUM.BOOKED_MEETING,
        BRAND_AMBASSADOR_STATUS_ENUM.PRE_ACCESS,
        BRAND_AMBASSADOR_STATUS_ENUM.WAITING_FOR_REVIEW,
      ]
    }

    return [status]
  }

  const fetchData = async () => {
    setIsLoading(true)
    try {
      const response = await getSpies({
        // Map order by to supported order by values
        statuses: convertStatuses(filters.status),
        first: resultsPerPage,
        skip: offset,
        cityId,
        search,
      })
      const result = await response.json()

      if (result['errorMessage']) throw new Error(result['errorMessage'])
      if (result.errors && result.errors[0])
        throw new Error(result.errors[0].message)

      setData(marshalData(result.data))
      setTotalCount(result.data.city.spies.totalCount)
    } catch (e) {
      setData([])
      setTotalCount(0)
      throw e
    } finally {
      setIsLoading(false)
    }
  }

  const marshalData = data => {
    const rows = data.city.spies.nodes.map(node => {
      return {
        Name: `${node.firstName} ${node.lastName}`,
        'Signup Date': node.memberSince
          ? toLocaleNonAmericanDateString(new Date(node.memberSince))
          : null,
        'Phone Number': node.mobilePhone,
        Email: node.email,
        Status: <StatusLabel status={node.status} />,
        Actions: (
          <div className={classes.actionsCell}>
            <LinkView
              to={`${PATHS.main.dashboard}${
                PATHS.dashboard.cities
              }/${cityId}/spies/${node.id}`}
            />
            <LinkEdit
              to={`${PATHS.main.dashboard}${
                PATHS.dashboard.cities
              }/${cityId}/spies/${node.id}/edit`}
            />
            <ButtonDeleteInline
              onClickHandler={() =>
                dispatch(
                  showReduxModal({
                    title: 'Confirmation needed',
                    onConfirm: async () => {
                      await deleteAmbassador(node.id)
                      hideReduxModal()
                      resetReduxModal()
                      fetchData()
                    },
                    onCancel: () => {
                      hideReduxModal()
                      resetReduxModal()
                    },
                    children: (
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        justifyContent="center"
                        mt={3}
                        mb={3}
                      >
                        Are you sure you want to delete {node.firstName}{' '}
                        {node.lastName}?
                      </Box>
                    ),
                  }),
                )
              }
            />
          </div>
        ),
      }
    })
    return rows
  }

  const onOffsetChange = value => {
    if (value < totalCount && value >= 0) {
      setOffset(value)
    }
  }

  const onInviteSpy = async email => {
    let errorMessage
    const response = await InviteSpyByEmail({ cityId, email })

    const result = await response.json()

    if (result['errorMessage']) {
      errorMessage = result['errorMessage']
    }
    if (result.errors && result.errors[0]) {
      errorMessage = result.errors[0].message
    }
    if (!result.data.inviteSpy.success) {
      errorMessage = 'Some error'
    }

    fetchData()
    return {
      success: result.data.inviteSpy.success,
      error: errorMessage,
    }
  }

  const isShowErrorOnInviteSpy = useMemo(() => !cityEmail, [cityEmail])

  return (
    <View
      data={data}
      handleSearch={setSearch}
      setFilters={setFilters}
      search={search}
      filters={filters}
      isLoading={isLoading}
      onOffsetChange={onOffsetChange}
      resultsPerPage={resultsPerPage}
      setResultsPerPage={setResultsPerPage}
      totalCount={totalCount}
      offsetRecordsStart={offset}
      onInviteSpy={onInviteSpy}
      isShowErrorOnInviteSpy={isShowErrorOnInviteSpy}
    />
  )
}

export default injectStyles(styles)(List)
